/**
 * Technical Foundation certification from contest self-evaluation grades.
 * Levels follow scorecard grades (D / C / B / A / A+) — the certificate level is the
 * minimum grade across completed Foundation skills for that sport + age.
 * S is stored for later use but shown as A+ while hidden.
 */

import type { ContestGradeCode } from '../types/contestCatalog';
import { CONTEST_AGE_CATEGORIES, toVisibleContestGrade } from './contestCatalog';
import {
  type ContestSelfEvalDraft,
  getAllFoundationContests,
  loadSavedContestSelfEval,
  reassessSelfEvalDraftGrades,
  uniqueCatalogContests,
} from './contestSelfEvaluation';
import { RELEASE_SPORTS } from '@config/releaseProfile';
import type { ContestCatalogEntry } from '../types/contestCatalog';

const GRADE_RANK: Record<string, number> = {
  D: 1,
  F: 1,
  C: 2,
  B: 3,
  A: 4,
  'A+': 5,
  S: 6,
};

const GRADE_SCORE: Record<string, number> = {
  D: 50,
  F: 45,
  C: 65,
  B: 75,
  A: 85,
  'A+': 92,
  S: 98,
};

export interface FoundationCertification {
  sport: string;
  ageCategory: string;
  /** Minimum grade across graded skills — drives certificate level. */
  levelCode: ContestGradeCode;
  levelLabel: string;
  /** Average mapped score 0–100 from skill grades. */
  scorePercent: number;
  skillCount: number;
  gradedCount: number;
  /** All required skills filled (when requiredIds provided). */
  complete: boolean;
  eligible: boolean;
  skillGrades: Array<{ contestId: string; grade: ContestGradeCode; value: string }>;
  updatedAt?: string;
}

export function gradeRank(code: string | null | undefined): number {
  if (!code) return 0;
  return GRADE_RANK[String(code)] ?? 0;
}

export function gradeToScorePercent(code: string | null | undefined): number {
  if (!code) return 0;
  return GRADE_SCORE[String(code)] ?? 0;
}

function minGrade(codes: ContestGradeCode[]): ContestGradeCode {
  let minRank = Infinity;
  let minCode: ContestGradeCode = 'C';
  codes.forEach((code) => {
    const r = gradeRank(code);
    if (r > 0 && r < minRank) {
      minRank = r;
      minCode = code;
    }
  });
  return minCode;
}

export function foundationCertLevelLabel(levelCode: ContestGradeCode): string {
  const visible = toVisibleContestGrade(levelCode) as ContestGradeCode;
  if (visible === 'A+') {
    return 'Technical Foundation — Distinction (A+)';
  }
  if (levelCode === 'A') return 'Technical Foundation — Level A';
  if (levelCode === 'B') return 'Technical Foundation — Level B';
  if (levelCode === 'C') return 'Technical Foundation — Level C';
  return `Technical Foundation — Level ${levelCode}`;
}

/**
 * Resolve certification from a self-eval draft.
 * Level = minimum grade among skills that have both a value and a grade.
 * Pass requiredContestIds to gate eligibility on full Foundation set completion.
 */
export function resolveFoundationCertification(
  draft: ContestSelfEvalDraft | null | undefined,
  requiredContestIds?: string[],
  liveContests?: ContestCatalogEntry[],
): FoundationCertification | null {
  if (!draft || draft.kind !== 'contest_self_evaluation') return null;

  const catalogContests =
    liveContests && liveContests.length
      ? liveContests
      : uniqueCatalogContests(getAllFoundationContests(draft.sport).flatMap((group) => group.contests));
  const working = catalogContests.length
    ? reassessSelfEvalDraftGrades(draft, catalogContests, draft.ageCategory).draft
    : draft;

  const ids =
    requiredContestIds && requiredContestIds.length > 0
      ? requiredContestIds
      : Object.keys(working.entries || {});

  if (!ids.length) return null;

  const skillGrades: FoundationCertification['skillGrades'] = [];
  ids.forEach((contestId) => {
    const entry = working.entries[contestId];
    if (!entry) return;
    const value = String(entry.value || '').trim();
    if (!value || !entry.grade) return;
    skillGrades.push({
      contestId,
      grade: toVisibleContestGrade(entry.grade) as ContestGradeCode,
      value,
    });
  });

  const complete =
    requiredContestIds && requiredContestIds.length > 0
      ? requiredContestIds.every((id) => String(working.entries[id]?.value || '').trim() !== '')
      : skillGrades.length > 0 && skillGrades.length === ids.length;

  if (skillGrades.length === 0) return null;

  const levelCode = toVisibleContestGrade(minGrade(skillGrades.map((s) => s.grade))) as ContestGradeCode;
  const scoreSum = skillGrades.reduce((sum, s) => sum + gradeToScorePercent(s.grade), 0);
  const scorePercent = Math.round((scoreSum / skillGrades.length) * 10) / 10;

  return {
    sport: working.sport,
    ageCategory: working.ageCategory,
    levelCode,
    levelLabel: foundationCertLevelLabel(levelCode),
    scorePercent,
    skillCount: ids.length,
    gradedCount: skillGrades.length,
    complete: Boolean(complete),
    eligible: Boolean(complete) && skillGrades.length > 0,
    skillGrades,
    updatedAt: working.updatedAt,
  };
}

/** Scan saved self-evals for release sports × age bands. */
export function listFoundationCertificationsForUser(
  userId: string,
  sports: readonly string[] = RELEASE_SPORTS,
  ages: readonly string[] = CONTEST_AGE_CATEGORIES,
  contestsBySport?: Record<string, ContestCatalogEntry[]>,
): FoundationCertification[] {
  if (!userId) return [];
  const out: FoundationCertification[] = [];
  sports.forEach((sport) => {
    ages.forEach((age) => {
      const draft = loadSavedContestSelfEval(userId, sport, age);
      const live = contestsBySport?.[sport];
      const cert = resolveFoundationCertification(draft, undefined, live);
      if (cert && cert.gradedCount > 0) out.push(cert);
    });
  });
  return out.sort((a, b) => {
    const sportCmp = a.sport.localeCompare(b.sport);
    if (sportCmp !== 0) return sportCmp;
    return ages.indexOf(a.ageCategory) - ages.indexOf(b.ageCategory);
  });
}

export function foundationCertificateNo(cert: FoundationCertification, playerId?: string): string {
  const pid = String(playerId || 'player').replace(/[^a-zA-Z0-9]/g, '').slice(0, 8).toUpperCase() || 'PLAYER';
  const sport = cert.sport.slice(0, 3).toUpperCase();
  const age = cert.ageCategory.replace(/[^a-zA-Z0-9]/g, '');
  return `TF-${sport}-${age}-${cert.levelCode}-${pid}`;
}

/** Expose rank table for UI legends. */
export const FOUNDATION_CERT_LEVELS: Array<{ code: ContestGradeCode; label: string }> = [
  { code: 'D', label: 'Grade D' },
  { code: 'C', label: 'Grade C' },
  { code: 'B', label: 'Grade B' },
  { code: 'A', label: 'Grade A' },
  { code: 'A+', label: 'Grade A+' },
];
